import { sha512 } from 'js-sha512';
import markdownit from 'markdown-it';
import { hooks } from 'botframework-webchat-component';
import { Config, Tier } from './config';
import {
    EVENT_RESTART_CHATBOT,
    EXCLUDED_REHYDRATION_TENANTS,
    MYCHART,
    TENANTS,
} from './constants';
import {
    flushEventsAsync as flushAnalyticsEventsAsync,
    logEvent as logAnalyticsEvent,
} from './analytics';
import {
    Activity,
    BrandParentUrl,
    ConnectorToken,
    EncryptedSessionPayload,
    EncryptedSessionRequest,
    InitLoaderValues,
    Location,
    Session,
    TenantGreetingConfig,
    TenantName,
} from './types';
import { InputMode } from './hooks';
import { logEvent, logException, getCorrelationId } from './logging';
import {
    LiveChatConnectionStatus,
    getLiveChatService,
} from './liveChatService';
import botGreetingConfig from './resources/botGreetingConfig.json';
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
    updateLocalStorageItemTtl,
} from './utils/localStorage';
import { LIVE_CHAT_HEARTBEAT_INTENT } from './intents';

import { GraceFeatureFlags } from './featureFlags';
import { AmpFlagSet } from './amplitude';
import {
    updateMATestResultsMenuButtonAction,
    handleMAChatbotOutage,
} from './messageassist';
import attributionConfig from './resources/attributionConfig.json';

const { useRenderMarkdownAsHTML } = hooks;

export const ENCRYPTED_SESSION_PATH = '/encrypted-session';
const DIRECTLINE_SESSION_TOKEN_COOKIE_NAME = 'grace-dst';
const TOKEN_FROM_GET_COOKIE_NAME = 'grace-tfg';
const CHECKPOINT_WATERMARK_COOKIE_NAME = 'grace-checkpoint-watermark';
const CURRENT_WATERMARK_COOKIE_NAME = 'grace-current-watermark';
const LIVE_CHAT_JWT_COOKIE_NAME = 'grace-livechat-jwt';
export const REHYDRATION_COOKIE_EXP_MINUTES = 10;

// URL Params
const USER_NAME_PARAM = 'userName';
const USER_ID_PARAM = 'userId';
const USER_NAME_FALLBACK = 'you';

let activeSession: Session | undefined;
let currentWatermark = '';
let currentSessionToken: string | undefined;

export enum LiveChatEndConfirmation {
    showDisconnectAndClose,
    showDisconnectAndReset,
    hide,
}

export const isBlank = (str: string): boolean => {
    return !str || /^\s*$/.test(str);
};

export const getUrlParam = (param: string): string => {
    return decodeURIComponent(
        (window.location.search.match(`${param}=([^&]+)`) || [])[1] || ''
    );
};

export const appendWindowUrlParam = (param: string, value: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(param, value);
    window.history.replaceState(null, '', url);
};

export const getWindowObject = (key: string): any => {
    return (window as any)[key];
};

export const isValidUrl = (value: string): boolean => {
    if (isBlank(value)) {
        return false;
    }

    try {
        new URL(value);
        return true;
    } catch (_error) {
        return false;
    }
};

export const getOriginHost = (): string => {
    const optionsStr = localStorage.getItem('options') || '{}';
    const options = JSON.parse(optionsStr);
    const appHostOrigin = getUrlParam('appHostOrigin');
    const referrer = getUrlParam('referrer');

    if (
        referrer.includes('testWebsite.html') &&
        options.originHost !== undefined &&
        options.originHost.length > 0
    ) {
        return options.originHost;
    }

    if (appHostOrigin !== '') {
        return appHostOrigin;
    }

    return window.location.href.toLowerCase();
};

export const getCustomScheme = (): string => {
    const customScheme = getUrlParam('customScheme');
    return isBlank(customScheme) ? 'https' : customScheme;
};

export const getConversationIdFromToken = (
    connectorToken: string | undefined
): string | undefined => {
    if (!connectorToken) {
        return undefined;
    }
    try {
        const tokenPayload = JSON.parse(
            atob(connectorToken.split('.')[1])
        ) as ConnectorToken;
        return tokenPayload?.conv ?? undefined;
    } catch (_e) {
        // Gracefully handle malformed token
        return undefined;
    }
};

export const isCookieEnabled = (): boolean => {
    try {
        return window.navigator.cookieEnabled;
        // eslint-disable-next-line
    } catch (_er) { }
    return false;
};

export const getCheckpointWatermark = (featureFlags: AmpFlagSet): string => {
    let watermark: string;
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        watermark =
            getLocalStorageItem(
                `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`
            ) || '';
    } else {
        watermark =
            getCookie(
                `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`
            ) || '';
    }

    if (watermark) {
        const watermarkInt = parseInt(watermark);
        if (watermarkInt > 0) {
            return (watermarkInt - 1).toString();
        }
    }
    return '0';
};

export const getCurrentWatermark = (
    featureFlags: AmpFlagSet
): string | null => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return (
            getLocalStorageItem(
                `${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`
            ) || null
        );
    }
    return getCookie(`${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`);
};

export const getSessionToken = (featureFlags: AmpFlagSet): string | null => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return (
            getLocalStorageItem(
                `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`
            ) || null
        );
    }
    return getCookie(
        `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`
    );
};

export const getCurrentSessionToken = (): string | undefined => {
    return currentSessionToken;
};

export const getTokenFromGet = (featureFlags: AmpFlagSet): string | null => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return (
            getLocalStorageItem(
                `${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`
            ) || null
        );
    }
    return getCookie(`${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`);
};

export const getLiveChatJwt = (featureFlags: AmpFlagSet): string | null => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return (
            getLocalStorageItem(
                `${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`
            ) || null
        );
    }
    return getCookie(`${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`);
};

export const setCheckpointWatermark = (
    featureFlags: AmpFlagSet,
    value: string,
    tier: Tier
): void => {
    setRehydrationData(
        featureFlags,
        `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`,
        value,
        tier
    );
};

export const setCurrentWatermark = (
    featureFlags: AmpFlagSet,
    value: string,
    tier: Tier
): void => {
    setRehydrationData(
        featureFlags,
        `${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`,
        value,
        tier
    );
};

export const setSessionToken = (
    featureFlags: AmpFlagSet,
    value: string,
    tier: Tier
): void => {
    setRehydrationData(
        featureFlags,
        `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`,
        value,
        tier
    );
};

export const setTokenFromGet = (
    featureFlags: AmpFlagSet,
    session: Session,
    value: string | null,
    tier: Tier
): void => {
    if (value && isTokenFromGet(session) && !getTokenFromGet(featureFlags)) {
        setRehydrationData(
            featureFlags,
            `${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`,
            value,
            tier
        );
    }
};

export const setLiveChatJwt = (
    featureFlags: AmpFlagSet,
    value: string,
    tier: Tier
): void => {
    setRehydrationData(
        featureFlags,
        `${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`,
        value,
        tier
    );
};

export const isTokenFromGet = (session: Session): boolean => {
    return (
        session.generatedBy !== undefined &&
        session.generatedBy.toLowerCase().indexOf('get') >= 0
    );
};

export function isRehydratedMessage(
    featureFlags: AmpFlagSet,
    currWatermark?: string
): boolean {
    const watermarks = getWatermarksAsNumbers(featureFlags, currWatermark);
    if (isNaN(watermarks[0]) || isNaN(watermarks[1])) {
        return false;
    }
    if (watermarks[0] <= watermarks[1]) {
        return true;
    }
    return false;
}

export function isLatestRehydratedMessage(
    featureFlags: AmpFlagSet,
    currWatermark?: string
): boolean {
    const watermarks = getWatermarksAsNumbers(featureFlags, currWatermark);
    if (isNaN(watermarks[0]) || isNaN(watermarks[1])) {
        return false;
    }
    if (watermarks[0] === watermarks[1]) {
        return true;
    }
    return false;
}

function getWatermarksAsNumbers(
    featureFlags: AmpFlagSet,
    currWatermark?: string
): [number, number] {
    const watermarkCookie = getCurrentWatermark(featureFlags) || undefined;
    if (currWatermark && watermarkCookie) {
        return [parseInt(currWatermark), parseInt(watermarkCookie)];
    }
    try {
        const lastActivityId = sessionStorage.getItem('lastActivityId');
        if (isGenesysResume() && currWatermark && lastActivityId) {
            return [
                parseInt(currWatermark || 'NaN'),
                parseInt(lastActivityId as string),
            ];
        }
    } catch (_er) {
        // noop
    }
    return [NaN, NaN];
}

export function clearRehydrationData(tier: Tier): void {
    // Removed canClear check in session as it is always true.
    // We also don't want to require the token in order to clear cookies.
    // To avoid passing through feature flag in this case we invalidate cookies and local storage
    setRehydrationCookieExpirations(0, tier);
    try {
        removeRehydrationLocalStorageItems();
    } catch {
        // noop
    }
}

export function clearLiveChatCookies(tier: Tier): void {
    // Clear both storage types
    setLiveChatCookieExpirations(0, tier);
    try {
        removeLocalStorageItem(
            `${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`
        );
    } catch {
        // noop
    }
}

export const getBrand = (brandParentUrl: BrandParentUrl): string => {
    const brandUrlParam = getUrlParam('brand');
    const brands = Object.keys(brandParentUrl);
    if (brands.includes(brandUrlParam)) {
        return brandUrlParam;
    }
    const parentUrl = getUrlParam('referrer');
    if (brands.includes(parentUrl)) {
        return parentUrl;
    }
    if (isBlank(parentUrl) || parentUrl.includes('testWebsite.html')) {
        return '';
    }
    try {
        const url = new URL(parentUrl);
        if (
            url.origin.includes('mychartwa-staging.providence.org') ||
            url.origin.includes('mychartwa.providence.org')
        ) {
            return '';
        }
        for (const brand in brandParentUrl) {
            for (const u of brandParentUrl[brand]) {
                if (url.origin.includes(u)) {
                    return brand;
                }
            }
        }
    } catch (_err) {
        // ignore
    }
    return '';
};

export const getAppVersion = (): string => {
    // Using getUrlParam is not working for mobile apps so use the originHost
    const originHost = getOriginHost();
    return decodeURIComponent(
        (originHost.match('appversion=([^&]+)') || [])[1] || ''
    );
};

export const isUnified = (): boolean => {
    return getUrlParam('unified') === 'true';
};

export const isMychartEnabledUser = (): boolean => {
    return getUrlParam('mychartEnabledUser') === 'true';
};

export const isFaqSurveyOptionEnabled = (): boolean => {
    return getUrlParam('enableFaqSurveyOption') === 'true';
};

export const updateFaqSurveyOptionEnabled = (enabled: boolean) => {
    appendWindowUrlParam('enableFaqSurveyOption', `${enabled}`);
};

export const isSyntheticTest = (): boolean => {
    return getUrlParam('synthetic') === 'true';
};

export const getRecipientName = (): string => {
    return getUrlParam('recipient').replace(/\+/g, ' ');
};

export const getRecipientId = (): string => {
    return getUrlParam('recipientId').replace(/\+/g, ' ');
};

export const isValidJson = (text: any): boolean => {
    if (typeof text !== 'string') {
        return false;
    }
    try {
        JSON.parse(text);
        return true;
    } catch (error) {
        return false;
    }
};

export const jsonObjectIsEmpty = (jsonObj: any): boolean => {
    return jsonObj && Object.keys(jsonObj).length === 0;
};

export const toHex = (str: string): string => {
    let result = '';
    for (let i = 0; i < str.length; i++) {
        result += str.charCodeAt(i).toString(16).padStart(4, '0');
    }
    return result;
};

export const requestChatBotEncrypted = async (
    featureFlags: AmpFlagSet,
    config: Pick<Config, 'tier' | 'authServiceEndpoint' | 'brandParentUrl'>,
    healthSystemToken: string | null,
    sessionToken?: string,
    location?: Location
): Promise<Session> => {
    let res;
    try {
        const endpoint = config.authServiceEndpoint || '';
        const body: EncryptedSessionRequest = {
            userName: getUrlParam(USER_NAME_PARAM) || USER_NAME_FALLBACK,
            userId: getUrlParam(USER_ID_PARAM) || undefined,
            brand: getBrand(config.brandParentUrl),
            tier: config.tier,
            healthSystemToken: healthSystemToken || undefined,
            sessionToken: sessionToken,
            lat: location?.lat,
            long: location?.long,
        };
        // 'X-Request-Id' header Sanic uses for Correlation-id
        // I tried updating Grace-API's code to look for `x-correlation-id` header,
        // but that didn't work as correlation-id didn't match the value that is getting passed here
        res = await fetch(endpoint + ENCRYPTED_SESSION_PATH, {
            method: 'POST',
            headers: {
                'X-Request-Id': getCorrelationId(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
    } catch (error: any) {
        if (error instanceof Error) {
            // typescript type guard
            logException(
                'requestChatBotEncrypted - There was error received in this function',
                {
                    exception: error,
                }
            );
        }
    }

    if (!res || res.status >= 400) {
        logEvent({ name: 'Can not initiate the chat session' }, {});
        clearRehydrationData(config.tier);
        window.parent.postMessage('connection error', '*');
        handleMAChatbotOutage(getTenantName(), 'Direct Line Failed to Connect');

        // Returning the dummy session
        return {
            userId: '',
            userName: '',
            connectorToken: '',
            correlationId: '',
            sessionId: '',
        };
    }

    const jsonWebToken = await res.text();
    const jwtPayload = JSON.parse(
        atob(jsonWebToken.split('.')[1])
    ) as EncryptedSessionPayload;
    const session = {
        userId: jwtPayload.userId,
        userName: jwtPayload.userName,
        connectorToken: jwtPayload.connectorToken,
        canClear: jwtPayload.canClear,
        generatedBy: jwtPayload.generatedBy,
        correlationId: jwtPayload.correlationId || getCorrelationId(),
        sessionId: jwtPayload.sessionId || '',
        isNewConversation: jwtPayload.isNewConversation,
    } as Session;
    // This is just to clean up watermarks and other cookies before connecting to
    // a new conversation. The session token will be overwritten below either way.
    if (jwtPayload.isNewConversation) {
        clearRehydrationData(config.tier);
    }
    setSessionToken(featureFlags, jwtPayload.sessionToken, config.tier);
    currentSessionToken = jwtPayload.sessionToken;
    return session;
};

/**
 * This function hashes values from user's jwt in
 * a way that is consistent with CEP and the rasa-grace backend,
 * allowing us to correlate events across sessions and devices
 *  https://prov-dig.atlassian.net/wiki/spaces/CN/pages/37815562/Hash+JWT+s+sub+for+use+as+Amplitude+ID
 * @param jwt
 * @returns
 */
export const generateAmplitudeUserId = (jwt: string): string | undefined => {
    let userId: string | undefined;
    try {
        const decodedToken = JSON.parse(atob(jwt.split('.')[1]));
        const sub: string = decodedToken?.sub;
        if (!sub) {
            return undefined; // dont return value if no username
        }
        userId = sha512(sub);
    } catch (e) {
        logException('Error generating amplitude user id from JWT', {
            exception: new Error('Could not generate amplitude user id token'),
        });
    }
    return userId;
};

export const requestChatBot = async (
    featureFlags: AmpFlagSet,
    config: Pick<Config, 'tier' | 'authServiceEndpoint' | 'brandParentUrl'>,
    location?: Location
): Promise<Session> => {
    const { tier, authServiceEndpoint, brandParentUrl } = config;
    const userName = getUrlParam(USER_NAME_PARAM) || USER_NAME_FALLBACK;
    const userId = getUrlParam(USER_ID_PARAM);
    const brand = getBrand(brandParentUrl);
    let path = '/session';
    path += '?userName=' + userName;
    if (userId) {
        path += '&userId=' + userId;
    }
    path += '&brand=' + brand;
    path += '&tier=' + tier;

    if (location) {
        path = `${path}&lat=${location.lat}&long=${location.long}`;
    }

    let res;
    try {
        const correlationId = getCorrelationId();

        const endpoint = authServiceEndpoint || '';
        // 'X-Request-Id' header Sanic uses for Correlation-id
        // I tried updating Grace-API's code to look for `x-correlation-id` header,
        // but that didn't work as correlation-id didn't match the value that is getting passed here
        res = await fetch(endpoint + path, {
            method: 'POST',
            headers: {
                'X-Request-Id': correlationId,
            },
        });
    } catch (e) {
        logException('Error getting correlation Id', {
            exception: new Error('Could not generate correlation Id'),
        });
    }

    if (!res || res.status >= 400) {
        console.log('Can not initiate the chat session');
        window.parent.postMessage('connection error', '*');

        // Returning the dummy session
        return {
            userId: '',
            userName: '',
            connectorToken: '',
            correlationId: '',
            sessionId: '',
        };
    }
    const jsonWebToken = await res.text();
    const session = JSON.parse(atob(jsonWebToken.split('.')[1])) as Session;
    setSessionToken(featureFlags, jsonWebToken, tier);
    return session;
};

export const getCookie = (name: string): string | null => {
    const match = document.cookie.match(
        new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? match[2] : null;
};

export const getCookieAsync = async (
    name: string,
    retries: number,
    delayRetry: number
): Promise<string | null> => {
    const value = getCookie(name);

    if (value !== null) {
        return Promise.resolve(value);
    }

    if (retries <= 0) {
        return Promise.resolve(null);
    }

    await new Promise((f) => setTimeout(f, delayRetry));

    return getCookieAsync(name, retries - 1, delayRetry);
};

export const getToken = (featureFlags: AmpFlagSet): string | null => {
    return getTokenFromGet(featureFlags) || getSessionToken(featureFlags);
};

export const setCookie = (
    name: string,
    value: string,
    expMinutes: number,
    path: string,
    tier: Tier
): void => {
    const cookieStr = name + '=' + value;
    const pathStr = '; path=' + path;
    const expires = new Date();
    expires.setTime(expires.getTime() + expMinutes * 60 * 1000);
    const expiresStr = '; expires=' + expires.toUTCString();
    let security = '';
    if (tier !== 'local') {
        security += '; SameSite=None; Secure';
    }
    document.cookie = cookieStr + expiresStr + pathStr + security;
};

export const setRehydrationData = (
    featureFlags: AmpFlagSet,
    name: string,
    value: string,
    tier: Tier
): void => {
    if (!value || !featureFlags[GraceFeatureFlags.DirectLineReconnect]) {
        return;
    }
    if (EXCLUDED_REHYDRATION_TENANTS.includes(getTenantName())) {
        return;
    }

    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        const ttlMs = REHYDRATION_COOKIE_EXP_MINUTES * 60 * 1000;
        setLocalStorageItem(name, value, ttlMs);
        return;
    }
    setCookie(name, value, REHYDRATION_COOKIE_EXP_MINUTES, '/', tier);
};

export const setRehydrationExpirations = (
    featureFlags: AmpFlagSet,
    expMinutes: number,
    tier: Tier
): void => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        const newExpTtlMs = expMinutes * 60 * 1000;
        updateLocalStorageItemTtl(
            `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`,
            newExpTtlMs
        );
        updateLocalStorageItemTtl(
            `${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`,
            newExpTtlMs
        );
        updateLocalStorageItemTtl(
            `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`,
            newExpTtlMs
        );
        updateLocalStorageItemTtl(
            `${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`,
            newExpTtlMs
        );
        updateLocalStorageItemTtl(
            `${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`,
            newExpTtlMs
        );
        return;
    }
    setRehydrationCookieExpirations(expMinutes, tier);
};

export const setRehydrationCookieExpirations = (
    expMinutes: number,
    tier: Tier
): void => {
    const names = [
        `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`,
        `${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`,
        `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`,
        `${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`,
    ];
    for (const name of names) {
        const cookie = getCookie(name) || undefined;
        if (cookie) {
            setCookie(name, cookie, expMinutes, '/', tier);
        }
    }
    setLiveChatCookieExpirations(expMinutes, tier);
};

export const removeRehydrationLocalStorageItems = (): void => {
    removeLocalStorageItem(
        `${getTenantName()}:${DIRECTLINE_SESSION_TOKEN_COOKIE_NAME}`
    );
    removeLocalStorageItem(`${getTenantName()}:${TOKEN_FROM_GET_COOKIE_NAME}`);
    removeLocalStorageItem(
        `${getTenantName()}:${CHECKPOINT_WATERMARK_COOKIE_NAME}`
    );
    removeLocalStorageItem(
        `${getTenantName()}:${CURRENT_WATERMARK_COOKIE_NAME}`
    );
    removeLocalStorageItem(`${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`);
};

export const setLiveChatCookieExpirations = (
    expMinutes: number,
    tier: Tier
): void => {
    const name = `${getTenantName()}:${LIVE_CHAT_JWT_COOKIE_NAME}`;
    const cookie = getCookie(name) || undefined;
    if (cookie) {
        setCookie(name, cookie, expMinutes, '/', tier);
    }
};

export const setEntrypointUtterance = (utterance: string) => {
    const entrypoint = `utterance:${utterance.replaceAll(' ', '+')}`;
    appendWindowUrlParam('entrypoint', encodeURI(entrypoint));
};

export const getGreetingConfigForTenant = (featureFlags: any): any => {
    if (jsonObjectIsEmpty(botGreetingConfig)) {
        logEvent({ name: 'No bot greeting config present' });
        return undefined;
    }
    const greetingFlag = GraceFeatureFlags.MessageAssistGreetingOptimization;
    if (!featureFlags[greetingFlag]) {
        logEvent({ name: `Feature flag disabled: ${greetingFlag}` });
        return undefined;
    }
    const tenantName = getTenantName();
    const tenantGreetingConfig = (botGreetingConfig as any)[tenantName];
    updateMATestResultsMenuButtonAction(
        tenantName,
        tenantGreetingConfig,
        featureFlags
    );
    if (jsonObjectIsEmpty(tenantGreetingConfig)) {
        logEvent({
            name: `No greeting config defined for tenant: ${tenantName}`,
        });
        return undefined;
    }
    logEvent({
        name: `Successfully pulled greeting config for tenant: ${tenantName}`,
    });
    return tenantGreetingConfig;
};

export const getGreetingConfigMessages = (
    greetingConfig?: TenantGreetingConfig
): Activity[] | undefined => {
    if (!greetingConfig?.messages || greetingConfig.messages.length == 0) {
        return undefined;
    }
    return greetingConfig.messages;
};

export const getGreetingConfigMainMenu = (
    greetingConfig?: TenantGreetingConfig
): Activity | undefined => {
    if (!greetingConfig?.mainMenu) {
        return undefined;
    }
    return greetingConfig.mainMenu;
};

export const getTenantName = (): TenantName => {
    const tenant = getUrlParam('tenant');
    // @ts-ignore - we are doing a value check here to make sure the type matches.
    return Object.values(TENANTS).includes(tenant)
        ? (tenant as TenantName)
        : TENANTS.COVID;
};

let chatLoaded = false;
export const postChatActivity = (): void => {
    window.parent.postMessage('chat interaction', '*');
    if (!chatLoaded) {
        window.parent.postMessage('chat loaded', '*');
        chatLoaded = true;
    }
};

const postLiveChatActivity = (): void => {
    window.parent.postMessage('live chat interaction', '*');
};

export const isMobile: () => boolean = () => {
    return getUrlParam('mobile') === 'true';
};

export const getPlatform = (): string => {
    const platform = getUrlParam('platform');

    if (platform && platform !== '') {
        return platform;
    }

    if (isUnified()) {
        return 'mobile';
    }

    return 'web';
};

export const useToastGreeting: () => boolean = () => {
    return getUrlParam('useToastGreeting') === 'true';
};

export const disableGreetingSuppressionOnLogin: () => boolean = () => {
    return getUrlParam('disableGreetingSuppressionOnLogin') === 'true';
};

export const isMobileBrowser: () => boolean = () => {
    const toMatch = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i,
    ];

    return toMatch.some((toMatchItem) => {
        return navigator.userAgent.match(toMatchItem);
    });
};

export async function openUrlForWebOrMobile(
    url: string,
    featureFlags: AmpFlagSet,
    mychartDomains: string[] | undefined,
    tier: Tier,
    newTab = true,
    closeChatBot: boolean | undefined = true
): Promise<void> {
    const mobile = isMobile();
    const goToUrlEvent = {
        name: 'GoToUrl',
        properties: { url, mobile },
    };

    logEvent(goToUrlEvent);

    if (mobile) {
        await flushAnalyticsEventsAsync();
        window.location.href = url;
    } else if (isUrlDirectMychartRedirect(url, mychartDomains)) {
        // eslint-disable-next-line
        await flushAnalyticsEventsAsync();
        parent.window.postMessage(`redirect:${url}`, '*');
    } else if (newTab) {
        window.open(url, '_blank');
        // If we are opening a new tab and not closing the bot then we don't need to flush events immediately
        if (closeChatBot !== false) {
            await flushAnalyticsEventsAsync();
        }
    } else {
        await flushAnalyticsEventsAsync();
        // eslint-disable-next-line
        parent.window.location.href = url;
    }

    if (closeChatBot !== false) {
        parent.window.postMessage('closeChatbot', '*');
    }
}

export async function openUrlForRedirect(
    url: string,
    featureFlags: AmpFlagSet,
    mychartDomains: string[] | undefined
): Promise<void> {
    await flushAnalyticsEventsAsync();

    if (isMobile()) {
        window.location.href = url;
    } else if (isUrlDirectMychartRedirect(url, mychartDomains)) {
        // eslint-disable-next-line
        parent.window.postMessage(`redirect:${url}`, '*');
    } else {
        window.open(url, '_blank');
    }
    parent.window.postMessage('closeChatbot', '*');
}

export function isUrlDirectMychartRedirect(
    url: string,
    mychartDomains: string[] | undefined
): boolean {
    if (!mychartDomains) {
        return false;
    }
    const parentUrl = getUrlParam('referrer');
    for (const domain of mychartDomains) {
        if (parentUrl.startsWith(domain) && url.startsWith(domain)) {
            return true;
        }
    }
    return false;
}

// FIXME: We should refactor how genesys resumes conversations to use rehydration
// For now this is handled separately
export const isGenesysResume: () => boolean = () => {
    return getUrlParam('resumeGenesys') === 'true';
};

export const cookieStorageAvailable = (tier: Tier) => {
    try {
        const testCookieValue = 'grace_test';
        setCookie(testCookieValue, '1', 1, '/', tier);
        return getCookie(testCookieValue) === '1';
    } catch (_er) {
        // noop
    }
    return false;
};

export const sessionStorageAvailable = () => {
    try {
        sessionStorage.getItem('session');
        return true;
    } catch (_er) {
        // noop
    }
    return false;
};

export const localStorageAvailable = () => {
    try {
        localStorage.setItem('grace_test', '1');
        const result = localStorage.getItem('grace_test') === '1';
        localStorage.removeItem('grace_test');
        return result;
    } catch (_er) {
        // noop
    }
    return false;
};

export function setupMessageListener(
    showFeedback: React.Dispatch<React.SetStateAction<boolean>>,
    setLiveChatEndConfirmation: React.Dispatch<
        React.SetStateAction<LiveChatEndConfirmation>
    >,
    interactedWithBot: boolean,
    sendEvent: (name: string, value: any) => void,
    tier: Tier
): () => void {
    const messageListener = (event: any): void => {
        if (event.data === 'closeChatbotRequested') {
            logAnalyticsEvent('Close Button Clicked');
            if (
                getLiveChatService().connectionStatus ===
                LiveChatConnectionStatus.disconnected
            ) {
                closeChatBot(interactedWithBot, showFeedback);
            } else {
                postLiveChatActivity();
                setLiveChatEndConfirmation(
                    LiveChatEndConfirmation.showDisconnectAndClose
                );
            }
            clearRehydrationData(tier);
        } else if (event.data === 'restartChatbotRequested') {
            if (
                getLiveChatService().connectionStatus ===
                LiveChatConnectionStatus.disconnected
            ) {
                sendEvent(EVENT_RESTART_CHATBOT, {});
                // Delay restart to allow restartChatbot event to send first
                setTimeout(() => {
                    restartChatbot(tier);
                }, 250);
            } else {
                postLiveChatActivity();
                setLiveChatEndConfirmation(
                    LiveChatEndConfirmation.showDisconnectAndReset
                );
            }
        }
    };
    window.addEventListener('message', messageListener);
    return () => {
        window.removeEventListener('message', messageListener);
    };
}

export async function getInitValuesFromLoader(
    validTokenOriginList: string[]
): Promise<InitLoaderValues | null> {
    const timeout = 1000;
    let values: InitLoaderValues | null = null;
    // eslint-disable-next-line
    const parentWindow = parent.window;
    if (parentWindow === window) {
        return null;
    }
    const messageListener = (event: any): void => {
        const isValid = validTokenOriginList.filter(
            (origin) => event.origin.startsWith(origin) || origin === '*'
        );
        if (isValid.length) {
            try {
                values = JSON.parse(event.data);
            } catch (_e) {
                // noop
            }
        }
    };
    window.addEventListener('message', messageListener);
    parentWindow.postMessage('initialize', '*');
    const start = new Date().getTime();
    while (start + timeout > new Date().getTime()) {
        if (values) {
            break;
        }
        await new Promise((resolve) => setTimeout(resolve, 10));
    }
    window.removeEventListener('message', messageListener);
    return values;
}

export const botMessageIsDropdown: (attachmentContent: any) => boolean = (
    attachmentContent
) =>
    !!attachmentContent.body &&
    !!attachmentContent.body.length &&
    attachmentContent.body[0].type === 'Input.ChoiceSet' &&
    !!attachmentContent.body[0].choices &&
    !!attachmentContent.body[0].choices.length;

export const getTextInputEnabled = (featureFlags: AmpFlagSet): InputMode => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return (getLocalStorageItem('inputenabled') || 'disabled') as InputMode;
    }
    return (getCookie('inputenabled') || 'disabled') as InputMode;
};

export const setTextInputEnabled = (
    featureFlags: AmpFlagSet,
    tier: Tier,
    value: string
) => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        return setLocalStorageItem(
            'inputenabled',
            value,
            REHYDRATION_COOKIE_EXP_MINUTES * 60 * 1000
        );
    }
    setCookie('inputenabled', value, REHYDRATION_COOKIE_EXP_MINUTES, '/', tier);
};

export const clearTextInputEnabled = (featureFlags: AmpFlagSet, tier: Tier) => {
    if (featureFlags[GraceFeatureFlags.DirectLineReconnectLocal]) {
        removeLocalStorageItem('inputenabled');
    }
    setCookie('inputenabled', 'disabled', 0, '/', tier);
};

export const setSession = (session: Session) => {
    activeSession = session;
};

export const getSession = (): Session | undefined => {
    return activeSession;
};

export const setWatermark = (watermark: string) => {
    currentWatermark = watermark;
};

export const getWatermark = (): string => {
    return currentWatermark;
};

export const isBillingLiveChatEnabled = (): boolean => {
    // Need to continue to support the enableLiveChat param until PAC-1912 is completed by the prov-app team
    return (
        getUrlParam('enableBillingLiveChat') === 'true' ||
        getUrlParam('enableLiveChat') === 'true'
    );
};

export const closeChatBot = (
    interactedWithBot: boolean,
    showFeedback?: React.Dispatch<React.SetStateAction<boolean>>
) => {
    if (interactedWithBot && showFeedback) {
        showFeedback(true);
    } else {
        const parentWindow = window.parent.window;
        if (parentWindow === window) {
            return;
        }
        parentWindow.postMessage('closeChatbot', '*');
    }
};

export const restartChatbot = (tier: Tier) => {
    logAnalyticsEvent('Reset Conversation');
    clearRehydrationData(tier);
    window.parent.postMessage('restartChatbot', '*');
};

export const isMyChartUrl = (url: string) => {
    /*
    Example url is https://mychartwa-staging.providence.org/mychart-wmtst/Scheduling
    urlSplitWithoutProtocol will contain mychartwa-staging.providence.org
    urlSplit with result in mychartwa
    urlSplitWithoutInstance returns mychart
    */
    if (isBlank(url)) {
        return false;
    }

    let urlSplitWithoutProtocol;
    try {
        if (url.indexOf('//') > -1) {
            urlSplitWithoutProtocol = url.split('/')[2];
        } else {
            urlSplitWithoutProtocol = url.split('/')[0];
        }
        const urlSplit = urlSplitWithoutProtocol?.split('.')[0]?.split('-')[0];
        const urlSplitWithoutMychartInstance = urlSplit?.substring(
            0,
            urlSplit.length - 2
        );
        return urlSplitWithoutMychartInstance === MYCHART;
    } catch (error: any) {
        logException(
            'isMyChartUrl - There was error received in this function',
            {
                exception: error,
            }
        );
        return false;
    }
};

// s("My name is {name} and I work as a {job}.", { name: "Ben", age: "sw dev"})
//   -> "My name is Ben and I work as a sw dev."
export const format = (
    str: string,
    values: { [key: string]: string | number } = {}
) => {
    const matchRegex = /{([a-zA-Z-_]+)}/;
    return str.replace(matchRegex, (match, param) =>
        param in values ? (values[param] as string) : match
    );
};

export const renderAsMarkdown = (text: string): string => {
    if (!text) {
        return text;
    }
    try {
        const renderMarkdownAsHTML = useRenderMarkdownAsHTML();
        return renderMarkdownAsHTML ? renderMarkdownAsHTML(text) : text;
    } catch {
        const md = markdownit();
        // Updating alternate markdown renderer to open hyperlinks in new tab.
        // Attributes match the attributes being set by botframework markdown renderer.
        // https://github.com/markdown-it/markdown-it/blob/master/docs/architecture.md#renderer
        const defaultRender =
            md.renderer.rules.link_open ||
            function (tokens, idx, options, env, self) {
                return self.renderToken(tokens, idx, options);
            };
        md.renderer.rules.link_open = function (
            tokens,
            idx,
            options,
            env,
            self
        ) {
            // Open hyperlinks in new tab
            tokens[idx].attrSet('rel', 'noopener noreferrer');
            tokens[idx].attrSet('target', '_blank');
            tokens[idx].attrSet('title', 'Opens in a new window; external.');
            return defaultRender(tokens, idx, options, env, self);
        };
        return md.render(text);
    }
};

export const ignoreMessageForDisableTextSend = (
    activity: Activity
): boolean => {
    if (activity?.text === LIVE_CHAT_HEARTBEAT_INTENT) {
        return true;
    }

    return false;
};

const getDomainFromIntent = (intent: string): string => {
    return intent.split('.')[0] || '';
};

/**
 * Adds attribution to an answer based on the source and intent.
 *
 * This function determines the appropriate attribution text to append to the given answer
 * by matching the intent and source against a predefined configuration (`attributionConfig`).
 * It first checks for an exact match for the domain (derived from the intent) and then for the source.
 * If no exact match is found, it falls back to partial matches or a default attribution.
 *
 * @param {string} [answer] - The original answer to which attribution will be added.
 * @param {string} [intent] - The intent of the user's query (e.g., "mychart.how-to-get-lab-results").
 * @param {string} [source] - The source of the information (e.g., "providence", "cdc").
 * @returns {string} - The answer with the appropriate attribution appended.
 *
 * @example
 * const answer = "You can view your lab results in MyChart.";
 * const intent = "mychart.how-to-get-lab-results";
 * const source = "providence";
 * const result = addAttributionToAnswer(answer, intent, source);
 * console.log(result);
 * // Output: "You can view your lab results in MyChart.\n___\n *Source: [Providence](https://mychart.providence.org)*"
 */
export const addAttributionToAnswer = (
    answer?: string,
    intent?: string,
    source?: string
): string => {
    if (!answer) {
        return '';
    }
    if (!intent) {
        return answer;
    }

    const domains = attributionConfig.providence;

    source = source?.toLowerCase() || '';

    let sources;

    // domain.intent
    // e.g. mychart.how-to-get-lab-results
    const domain = getDomainFromIntent(intent);

    sources = domains[domain as keyof typeof domains];
    if (!sources) {
        // if an exact domain match is not found
        // existing behavior is to check for a partial match
        // e.g. vaccination.what-vaccine-is-available
        const matchingIntentKey = Object.keys(domains).find((i) =>
            intent.includes(i.toLowerCase())
        );
        if (!matchingIntentKey) {
            return answer;
        }
        sources = domains[matchingIntentKey as keyof typeof domains];
    }

    let attribution = '';
    attribution = sources[source as keyof typeof sources];
    if (!attribution) {
        // if an exact source match is not found
        // existing behavior is to check for a partial match
        let matchingSourceKey = Object.keys(sources).find((s) =>
            source.includes(s.toLowerCase())
        );

        // if no partial match is found, check for default
        if (!matchingSourceKey) {
            matchingSourceKey = 'default';
        }
        if (matchingSourceKey in sources) {
            attribution = sources[matchingSourceKey as keyof typeof sources];
        }
    }

    return answer + attribution;
};
